import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { collection, query, where, getDocs, doc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import Swal from 'sweetalert2';

import UserNav from '../../components/navbar/user-nav';
import AdminSidebar from '../../components/admin-sidebar';
import Footer from '../../components/footer';
import FooterTop from '../../components/footer-top';
import PageTitle from '../../components/page-title';
import { LoadingMessage, EmptyState, SkeletonTable } from '../../components/loading-states';

interface Direccion {
  calle?: string;
  numero?: string;
  ciudad?: string;
  estado?: string;
  zona?: string;
  area?: string;
  unidad?: string;
  codigo_postal?: string;
}

interface Propiedad {
  id: string;
  nombre?: string;
  titulo?: string;
  direccion: string | Direccion;
  ciudad?: string;
  area?: string;
  comunidad?: string;
  precio?: number;
  precio_actual_usd?: number;
  precio_original_usd?: number;
  estado?: string;
  imagen?: string;
  fotos?: string[];
  descripcion?: string;
  tipo_operacion?: string;
  operacion?: string;
  tipo_propiedad?: string;
  tipo?: string;
  categoria?: string;
  tamaño?: string;
  estatus_publicacion?: string;
  activo?: boolean;
}

export default function MyProperty() {
  const [show, setShow] = useState(false);
  const [user, setUser] = useState<any>(null);
  const [propiedades, setPropiedades] = useState<Propiedad[]>([]);
  const [loading, setLoading] = useState(true);
  const [filtroEstatus, setFiltroEstatus] = useState<'todas' | 'publicada' | 'pendiente' | 'rechazada'>('todas');

  // 1. Obtener usuario
  useEffect(() => {
    const unsub = onAuthStateChanged(getAuth(), async (fbUser) => {
      if (!fbUser?.email) return;

      // Buscar en asociado
      let snap = await getDocs(query(collection(db, 'asociado'), where('correo', '==', fbUser.email)));
      if (!snap.empty) {
        const data = snap.docs[0].data();
        const id = snap.docs[0].id;
        const inmobiliariaDocId = data.inmobiliaria_doc_id; // Usar doc.id

        console.log('👤 Asociado encontrado:', data.nombre);
        console.log('🏢 inmobiliaria_doc_id:', inmobiliariaDocId);

        // Obtener nombre de inmobiliaria usando doc.id
        let nombreInmo = '';
        if (inmobiliariaDocId) {
          const { doc: docFn, getDoc } = await import('firebase/firestore');
          const inmoDocRef = docFn(db, 'inmobiliaria', inmobiliariaDocId);
          const inmoSnap = await getDoc(inmoDocRef);
          if (inmoSnap.exists()) {
            nombreInmo = inmoSnap.data().nombre || '';
            console.log('✅ Inmobiliaria encontrada:', nombreInmo);
          } else {
            console.warn('⚠️ Inmobiliaria no encontrada con doc.id:', inmobiliariaDocId);
          }
        }

        setUser({ ...data, tipo: 'asociado', id, inmobiliaria_nombre: nombreInmo });
        return;
      }

      // Buscar en asesor
      snap = await getDocs(query(collection(db, 'asesor'), where('correo', '==', fbUser.email)));
      if (!snap.empty) {
        const data = snap.docs[0].data();
        const id = snap.docs[0].id;
        const inmobiliariaDocId = Array.isArray(data.inmobiliaria_doc_ids)
          ? data.inmobiliaria_doc_ids[0]
          : data.inmobiliaria_doc_ids;

        console.log('👤 Asesor encontrado:', data.nombre);
        console.log('🏢 inmobiliaria_doc_ids:', inmobiliariaDocId);

        // Obtener nombre de inmobiliaria usando doc.id
        let nombreInmo = '';
        if (inmobiliariaDocId) {
          const { doc: docFn, getDoc } = await import('firebase/firestore');
          const inmoDocRef = docFn(db, 'inmobiliaria', inmobiliariaDocId);
          const inmoSnap = await getDoc(inmoDocRef);
          if (inmoSnap.exists()) {
            nombreInmo = inmoSnap.data().nombre || '';
            console.log('✅ Inmobiliaria encontrada:', nombreInmo);
          } else {
            console.warn('⚠️ Inmobiliaria no encontrada con doc.id:', inmobiliariaDocId);
          }
        }

        setUser({ ...data, tipo: 'asesor', id, inmobiliaria_nombre: nombreInmo });
      }
    });

    return () => unsub();
  }, []);

  // 2. Cargar propiedades usando doc.id de Firestore
useEffect(() => {
  const cargarPropiedades = async () => {
    if (!user) return;

    try {
      console.log('🔍 Buscando propiedades para:', {
        tipo: user.tipo,
        nombre: user.nombre,
        docId: user.id
      });

      // Usar el doc.id de Firestore del asesor/asociado
      // Traer TODAS las propiedades (activas, pendientes, rechazadas)
      const q = query(
        collection(db, 'propiedades'),
        where('asesor_doc_ids', 'array-contains', user.id)
      );

      const snap = await getDocs(q);
      const list = snap.docs.map(doc => ({ id: doc.id, ...doc.data() } as Propiedad));

      console.log(`✅ Propiedades encontradas: ${list.length}`);

      if (list.length === 0) {
        console.warn('⚠️ No se encontraron propiedades. Verificar que las propiedades tengan el campo asesor_doc_ids con el doc.id:', user.id);
      }

      setPropiedades(list);
    } catch (error) {
      console.error('❌ Error al obtener propiedades:', error);
    } finally {
      setLoading(false);
    }
  };

  cargarPropiedades();
}, [user]);

  // Función para cambiar el estado de la propiedad
  const handleStatusChange = async (propiedadId: string, nuevoEstado: string) => {
    try {
      const propRef = doc(db, 'propiedades', propiedadId);

      // Determinar si la propiedad debe estar activa
      const activo = nuevoEstado === 'Active';

      await updateDoc(propRef, {
        estatus_publicacion: nuevoEstado.toLowerCase(),
        activo: activo
      });

      // Actualizar el estado local
      setPropiedades(prev =>
        prev.map(prop =>
          prop.id === propiedadId
            ? { ...prop, estatus_publicacion: nuevoEstado.toLowerCase(), activo }
            : prop
        )
      );

      Swal.fire({
        icon: 'success',
        title: 'Estado actualizado',
        text: `La propiedad ahora está en estado: ${nuevoEstado}`,
        confirmButtonColor: '#3085d6',
        timer: 2000
      });
    } catch (error) {
      console.error('Error al actualizar estado:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'No se pudo actualizar el estado de la propiedad',
        confirmButtonColor: '#d33'
      });
    }
  };



  return (
    <>
      <UserNav
        nombreUsuario={user?.nombre || 'Desconocido'}
        nombreInmobiliaria={user?.inmobiliaria_nombre || 'Inmobiliaria no asignada'}
      />

      <PageTitle
        title="Mis Propiedades Publicadas"
        subtitle="Consulta, edita o destaca tus propiedades en venta o renta dentro del portal AMPI."
      />

      <section className="bg-light">
        <div className="container-fluid">
          <div className="row">
            <div className="col-lg-12 col-md-12">
              <div className="filter_search_opt">
                <button className="btn btn-dark full-width mb-4" onClick={() => setShow(!show)}>
                  Dashboard Navigation<i className="fa-solid fa-bars ms-2"></i>
                </button>
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col-lg-3 col-md-12">
              <AdminSidebar
                show={show}
                setShow={setShow}
                nombre={user?.nombre}
                inmobiliaria={user?.inmobiliaria_nombre}
              />
            </div>

            <div className="col-lg-9 col-md-12">
              <div className="dashboard-wraper">
                <div className="form-submit mb-4">
                  <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
                    <h4 className="mb-0">Mis Propiedades</h4>

                    {/* Filtro por estatus */}
                    <div className="btn-group" role="group">
                      <button
                        type="button"
                        className={`btn btn-sm ${filtroEstatus === 'todas' ? 'btn-primary' : 'btn-outline-primary'}`}
                        onClick={() => setFiltroEstatus('todas')}
                      >
                        Todas ({propiedades.length})
                      </button>
                      <button
                        type="button"
                        className={`btn btn-sm ${filtroEstatus === 'publicada' ? 'btn-success' : 'btn-outline-success'}`}
                        onClick={() => setFiltroEstatus('publicada')}
                      >
                        <i className="fa-solid fa-circle-check me-1"></i>
                        Publicadas ({propiedades.filter(p => p.estatus_publicacion === 'aprobada' || p.activo === true).length})
                      </button>
                      <button
                        type="button"
                        className={`btn btn-sm ${filtroEstatus === 'pendiente' ? 'btn-warning' : 'btn-outline-warning'}`}
                        onClick={() => setFiltroEstatus('pendiente')}
                      >
                        <i className="fa-solid fa-clock me-1"></i>
                        Pendientes ({propiedades.filter(p => p.estatus_publicacion === 'pendiente').length})
                      </button>
                      <button
                        type="button"
                        className={`btn btn-sm ${filtroEstatus === 'rechazada' ? 'btn-danger' : 'btn-outline-danger'}`}
                        onClick={() => setFiltroEstatus('rechazada')}
                      >
                        <i className="fa-solid fa-circle-xmark me-1"></i>
                        Rechazadas ({propiedades.filter(p => p.estatus_publicacion === 'rechazada').length})
                      </button>
                    </div>
                  </div>
                </div>

                {loading ? (
                  <SkeletonTable rows={5} columns={6} />
                ) : propiedades.length === 0 ? (
                  <EmptyState
                    icon="fa-solid fa-building"
                    title="Sin propiedades"
                    message="No tienes propiedades registradas aún"
                    actionText="Agregar Propiedad"
                    onAction={() => window.location.href = '/submit-property-dashboard'}
                  />
                ) : (
                  <div className="row">
                    {propiedades
                      .filter(item => {
                        if (filtroEstatus === 'todas') return true;
                        if (filtroEstatus === 'publicada') return item.estatus_publicacion === 'aprobada' || item.activo === true;
                        if (filtroEstatus === 'pendiente') return item.estatus_publicacion === 'pendiente';
                        if (filtroEstatus === 'rechazada') return item.estatus_publicacion === 'rechazada';
                        return true;
                      })
                      .map((item, index) => (
                      <div className="col-md-12 mb-3" key={index}>
                        <div className="card shadow-sm">
                          <div className="row g-0">
                            {/* Imagen de la propiedad */}
                            <div className="col-md-4">
                              {item.fotos && item.fotos.length > 0 && item.fotos[0] ? (
                                <img
                                  src={item.fotos[0]}
                                  className="img-fluid h-100 w-100"
                                  alt={item.titulo || item.nombre}
                                  style={{ objectFit: 'cover', minHeight: '200px', maxHeight: '250px' }}
                                  onError={(e) => {
                                    const target = e.target as HTMLImageElement;
                                    target.src = '/default.jpg';
                                  }}
                                />
                              ) : (
                                <div
                                  className="bg-secondary d-flex align-items-center justify-content-center h-100"
                                  style={{ minHeight: '200px' }}
                                >
                                  <i className="fas fa-home fa-3x text-white"></i>
                                </div>
                              )}
                            </div>

                            {/* Información de la propiedad */}
                            <div className="col-md-8">
                              <div className="card-body d-flex flex-column h-100">
                                <div className="d-flex justify-content-between align-items-start mb-2">
                                  <h5 className="card-title mb-0">
                                    <Link to={`/view-property/${item.id}`} className="text-primary text-decoration-none">
                                      {item.titulo || item.nombre || 'Propiedad sin título'}
                                    </Link>
                                  </h5>
                                  <div className="action d-flex gap-2">
                                    <Link to={`/view-property/${item.id}`} title="Ver" className="btn btn-sm btn-outline-primary">
                                      <i className="fa-regular fa-eye"></i>
                                    </Link>
                                    {user?.tipo === 'asociado' && (
                                      <>
                                        <Link to={`/edit-property/${item.id}`} title="Editar" className="btn btn-sm btn-outline-warning">
                                          <i className="fa-solid fa-pen-to-square"></i>
                                        </Link>
                                        <div className="btn-group">
                                          <button
                                            type="button"
                                            className={`btn btn-sm dropdown-toggle ${
                                              item.estatus_publicacion === 'active' || item.activo
                                                ? 'btn-success'
                                                : item.estatus_publicacion === 'pending'
                                                ? 'btn-warning'
                                                : 'btn-danger'
                                            }`}
                                            data-bs-toggle="dropdown"
                                            aria-expanded="false"
                                            title="Cambiar estado"
                                          >
                                            <i className={`fa-solid ${
                                              item.estatus_publicacion === 'active' || item.activo
                                                ? 'fa-circle-check'
                                                : item.estatus_publicacion === 'pending'
                                                ? 'fa-clock'
                                                : 'fa-circle-xmark'
                                            } me-1`}></i>
                                            {item.estatus_publicacion === 'active' || item.activo
                                              ? 'Active'
                                              : item.estatus_publicacion === 'pending'
                                              ? 'Pending'
                                              : 'Closed'}
                                          </button>
                                          <ul className="dropdown-menu">
                                            <li>
                                              <button
                                                className="dropdown-item"
                                                onClick={() => handleStatusChange(item.id, 'Active')}
                                              >
                                                <i className="fa-solid fa-circle-check me-2" style={{ color: '#198754' }}></i>
                                                <span style={{ color: '#198754' }}>Active</span>
                                              </button>
                                            </li>
                                            <li>
                                              <button
                                                className="dropdown-item"
                                                onClick={() => handleStatusChange(item.id, 'Pending')}
                                              >
                                                <i className="fa-solid fa-clock me-2" style={{ color: '#ffc107' }}></i>
                                                <span style={{ color: '#ffc107' }}>Pending</span>
                                              </button>
                                            </li>
                                            <li>
                                              <button
                                                className="dropdown-item"
                                                onClick={() => handleStatusChange(item.id, 'Closed')}
                                              >
                                                <i className="fa-solid fa-circle-xmark me-2" style={{ color: '#dc3545' }}></i>
                                                <span style={{ color: '#dc3545' }}>Closed</span>
                                              </button>
                                            </li>
                                          </ul>
                                        </div>
                                      </>
                                    )}
                                  </div>
                                </div>

                                {/* Ubicación */}
                                <p className="mb-2">
                                  <i className="fa-solid fa-map-marker-alt me-2 text-primary"></i>
                                  <span>
                                    {item.area ||
                                     item.comunidad ||
                                     item.ciudad ||
                                     (typeof item.direccion === 'object' && item.direccion
                                       ? `${item.direccion.calle || ''} ${item.direccion.numero || ''}, ${item.direccion.ciudad || item.direccion.area || ''}`.trim()
                                       : typeof item.direccion === 'string'
                                       ? item.direccion
                                       : 'Ubicación no especificada')}
                                  </span>
                                  {item.area && item.comunidad && <span> - {item.comunidad}</span>}
                                </p>

                                {/* Descripción */}
                                {item.descripcion && (
                                  <p
                                    className="mb-2"
                                    style={{
                                      fontSize: '0.9rem',
                                      whiteSpace: 'nowrap',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      color: '#000'
                                    }}
                                    title={item.descripcion}
                                  >
                                    {item.descripcion}
                                  </p>
                                )}

                                {/* Badges de estado y tipo */}
                                <div className="mb-2">
                                  {/* Badge de estatus de publicación - visible para todos */}
                                  <span
                                    className={`badge me-2 ${
                                      item.estatus_publicacion === 'aprobada' || item.activo === true
                                        ? 'bg-success'
                                        : item.estatus_publicacion === 'pendiente'
                                        ? 'bg-warning text-dark'
                                        : item.estatus_publicacion === 'rechazada'
                                        ? 'bg-danger'
                                        : 'bg-secondary'
                                    }`}
                                  >
                                    <i className={`fa-solid ${
                                      item.estatus_publicacion === 'aprobada' || item.activo === true
                                        ? 'fa-circle-check'
                                        : item.estatus_publicacion === 'pendiente'
                                        ? 'fa-clock'
                                        : item.estatus_publicacion === 'rechazada'
                                        ? 'fa-circle-xmark'
                                        : 'fa-circle-question'
                                    } me-1`}></i>
                                    {item.estatus_publicacion === 'aprobada' || item.activo === true
                                      ? 'Publicada'
                                      : item.estatus_publicacion === 'pendiente'
                                      ? 'Pendiente'
                                      : item.estatus_publicacion === 'rechazada'
                                      ? 'Rechazada'
                                      : 'Sin estatus'}
                                  </span>
                                  {(item.tipo_operacion || item.operacion || item.estado) && (
                                    <span
                                      className={`badge ${
                                        (item.tipo_operacion || item.operacion || item.estado || '').toLowerCase().includes('venta')
                                          ? 'bg-success'
                                          : 'bg-info'
                                      } me-2`}
                                    >
                                      {item.tipo_operacion || item.operacion || item.estado}
                                    </span>
                                  )}
                                  {(item.tipo_propiedad || item.tipo || item.categoria) && (
                                    <span className="badge bg-secondary">
                                      {item.tipo_propiedad || item.tipo || item.categoria}
                                    </span>
                                  )}
                                </div>

                                {/* Precio */}
                                <p className="fw-bold text-primary mb-0" style={{ fontSize: '1.1rem' }}>
                                  {item.precio_actual_usd
                                    ? `$${item.precio_actual_usd.toLocaleString()} USD`
                                    : item.precio_original_usd
                                    ? `$${item.precio_original_usd.toLocaleString()} USD`
                                    : item.precio
                                    ? `$${item.precio.toLocaleString()}`
                                    : 'Precio no disponible'}
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      <FooterTop bg="theme-bg" />
      <Footer />
    </>
  );
}
